import type { InventoryListByIdAPI } from '@openx/types/inventoryList';
import type { AppInventoryVersionType } from '@openx/types/targeting/appInventory';
import type {
  InstanceProps,
  InventoryAndContentState,
  InventoryContentOption,
  ParentAccountFeatures,
} from '@openx/types/targeting/inventoryAndContent';
import { ComparisonType } from '@openx/types/targeting/targetingValuesTypes';
import { noop } from '@openx/utils/noop';
import { type Dispatch, type SetStateAction, useState } from 'react';
import { useTargetingContext } from '../../../utils/context/TargetingContext';
import type { InvalidItemsError } from '../../shared/BulkAdd/types';
import { useBulkAdd } from '../../shared/BulkAdd/useBulkAdd';
import { TopBoxRenderer } from '../../shared/InventoryAndContent/components/TopBoxRenderer';
import { INVENTORY_LIMITS } from '../../shared/InventoryAndContent/constants';
import { useAppInventoryTopBox } from '../../shared/InventoryAndContent/hooks/useAppInventoryTopBox';
import type { TargetingItemsProps } from '../../types';
import { type AppInventoryProps, optionsWithBulkAdd } from './constants';
import { useGetDrawerContent } from './hooks/useGetDrawerContent';
import { useSetupInventoryAndContent } from './hooks/useSetupInventoryAndContent';
import { BulkDrawer } from './partials/BulkDrawer';

type InventoryTopboxProps = {
  inputInstanceValue: InstanceProps | null;
  setInputInstanceValue: Dispatch<SetStateAction<InstanceProps | null>>;
  isBefInstance: boolean;
  parentAccountFeatures: ParentAccountFeatures;
  setAppBundleIdDuplicateErrors: Dispatch<SetStateAction<InvalidItemsError | null>>;
  inventoryListItems?: InventoryListByIdAPI['values'];
  contentOption: InventoryContentOption;
  setContentOption: Dispatch<SetStateAction<InventoryContentOption>>;
  inventoryVersion?: AppInventoryVersionType;
} & AppInventoryProps &
  TargetingItemsProps<InventoryAndContentState>;

export const InventoryAndContentTopBox = ({
  targetingParams,
  inputInstanceValue,
  isBefInstance,
  parentAccountFeatures,
  dispatchInventory = noop,
  dispatchInstance = noop,
  setInputInstanceValue,
  accountUid = '',
  setAppBundleIdDuplicateErrors,
  inventoryListItems = [],
  displayContext,
  contentOption,
  setContentOption,
  inventoryVersion,
  // App Inventory specific props (from parent)
  appInventoryType: appInventoryTypeProp,
  setAppInventoryType: setAppInventoryTypeProp,
  appNameComparisonType: appNameComparisonTypeProp,
  setAppNameComparisonType: setAppNameComparisonTypeProp,
  bundleIdComparisonType: bundleIdComparisonTypeProp,
  setBundleIdComparisonType: setBundleIdComparisonTypeProp,
}: InventoryTopboxProps) => {
  const [comparisonType, setComparisonType] = useState<ComparisonType>(ComparisonType.EQUALS);

  // App Name Bulk Validation backed by appGroupSearch (always on; safe default if context hook is missing)
  const { useAppNameBulkValidationV2 } = useTargetingContext();
  const useValidationHook =
    useAppNameBulkValidationV2 ??
    (() => ({
      validateAppNames: async () => ({ errors: [], validItems: [] }),
      isReady: false,
    }));
  const { validateAppNames: appNameValidationV2, isReady: isValidationReady } = useValidationHook();

  const {
    effectiveContentOption,
    effectiveComparisonType,
    publisherInventoryRuleOptions,
    setPublisherInventoryRuleOptions,
    appInventoryType,
    setAppInventoryType,
    appNameComparisonType,
    bundleIdComparisonType,
  } = useAppInventoryTopBox<InventoryContentOption>({
    comparisonType,
    contentOption,
    appInventoryTypeProp,
    setAppInventoryTypeProp,
    appNameComparisonTypeProp,
    setAppNameComparisonTypeProp,
    bundleIdComparisonTypeProp,
    setBundleIdComparisonTypeProp,
  });

  const { addItems, customValidation, itemsCount, comparisonFieldCriteria } = useSetupInventoryAndContent({
    targetingParams,
    contentOption: effectiveContentOption, // Use effectiveContentOption to respect App Inventory type selection
    comparisonType: effectiveComparisonType, // Use effectiveComparisonType to respect App Inventory type selection
    isBefInstance,
    dispatchInventory,
    publisherInventoryRuleOptions,
    setPublisherInventoryRuleOptions,
    displayContext,
  });

  const { isBulkOpen, onBulkAdd, onBulkClose, onBulkOpen } = useBulkAdd({ onAdd: addItems });

  const isLimitReached = itemsCount >= INVENTORY_LIMITS[contentOption];
  const showBulkAdd = optionsWithBulkAdd.includes(contentOption);

  const { drawerContent: drawerContentBase } = useGetDrawerContent({
    dispatchInventory,
    targetingParams,
    contentOption,
    comparisonType,
    setComparisonType,
    dispatchInstance,
    inputInstanceValue,
    isBefInstance,
    setInputInstanceValue,
    onBulkOpen,
    // App Inventory specific props
    appInventoryType,
    setAppInventoryType,
    appNameComparisonType,
    bundleIdComparisonType,
    accountUid,
    setAppBundleIdDuplicateErrors,
  });

  return (
    <>
      <TopBoxRenderer
        comparisonType={comparisonType}
        contentOption={contentOption}
        parentAccountFeatures={parentAccountFeatures}
        showBulkAdd={showBulkAdd}
        isLimitReached={isLimitReached}
        targetingParams={targetingParams}
        setContentOption={setContentOption}
        setComparisonType={setComparisonType}
        dispatchInventory={dispatchInventory}
        onBulkOpen={onBulkOpen}
        accountUid={accountUid}
        setAppBundleIdDuplicateErrors={setAppBundleIdDuplicateErrors}
        inventoryVersion={inventoryVersion}
        drawerContentBase={drawerContentBase}
        // App Inventory specific props
        appInventoryType={appInventoryType}
      />

      {isBulkOpen && effectiveComparisonType && (
        <BulkDrawer
          contentOption={contentOption}
          itemsCount={itemsCount}
          comparisonFieldCriteria={comparisonFieldCriteria}
          comparisonType={effectiveComparisonType}
          setContentOption={setContentOption}
          setComparisonType={setComparisonType}
          onBulkAdd={onBulkAdd}
          onBulkClose={onBulkClose}
          customValidation={customValidation}
          parentAccountFeatures={parentAccountFeatures}
          inventoryListItems={inventoryListItems}
          publisherInventoryRuleOptions={publisherInventoryRuleOptions}
          setPublisherInventoryRuleOptions={setPublisherInventoryRuleOptions}
          targetingParams={targetingParams}
          displayContext={displayContext}
          appInventoryType={appInventoryType}
          appNameValidationV2={isValidationReady ? appNameValidationV2 : undefined}
          inventoryVersion={inventoryVersion}
        />
      )}
    </>
  );
};
